import { useState } from 'react';
import { Building2, Hash, ShieldCheck, Users, User, CreditCard, ChevronRight, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SeriesTab } from './SeriesTab';
import { CertificateTab } from './CertificateTab';
import { CompanyTab } from './CompanyTab';
import { UsersTab } from './UsersTab';
import { AccountTab } from './AccountTab';
import { BillingTab } from '@/features/billing/BillingTab';

interface TabDef {
  id: string;
  label: string;
  desc: string;
  icon: LucideIcon;
}

const TABS: TabDef[] = [
  { id: 'empresa', label: 'Empresa', desc: 'Datos fiscales', icon: Building2 },
  { id: 'series', label: 'Series', desc: 'Comprobantes', icon: Hash },
  { id: 'certificate', label: 'Certificado SUNAT', desc: 'Firma electrónica', icon: ShieldCheck },
  { id: 'usuarios', label: 'Usuarios', desc: 'Equipo y permisos', icon: Users },
  { id: 'cuenta', label: 'Mi cuenta', desc: 'Perfil y contraseña', icon: User },
  { id: 'billing', label: 'Suscripción', desc: 'Plan y pagos', icon: CreditCard },
];

type TabId = (typeof TABS)[number]['id'];

const CONTENT: Record<TabId, JSX.Element> = {
  empresa: <CompanyTab />,
  series: <SeriesTab />,
  certificate: <CertificateTab />,
  usuarios: <UsersTab />,
  cuenta: <AccountTab />,
  billing: <BillingTab />,
};

export function SettingsPage(): JSX.Element {
  const [active, setActive] = useState<TabId>('empresa');

  return (
    <div className="cx-main min-h-full px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-[1320px]">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-[28px] font-extrabold tracking-tight text-foreground">Configuración</h1>
          <p className="text-sm sm:text-[15px] text-muted-foreground mt-1.5">Administra tu empresa, usuarios y plan.</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* Menú vertical (sticky, como el diseño) */}
          <nav className="flex lg:flex-col gap-1 lg:w-[248px] lg:shrink-0 overflow-x-auto lg:overflow-visible lg:sticky lg:top-0 lg:self-start">
            {TABS.map((t) => {
              const on = active === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActive(t.id)}
                  className={cn(
                    'group flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all shrink-0 lg:w-full',
                    on ? 'border-border bg-card shadow-sm' : 'border-transparent hover:bg-card hover:border-border',
                  )}
                >
                  <span
                    className={cn(
                      'h-9 w-9 rounded-[10px] grid place-items-center shrink-0 transition-colors',
                      on ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    <t.icon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="hidden lg:flex flex-col min-w-0 flex-1">
                    <span className="text-sm font-semibold text-foreground whitespace-nowrap">{t.label}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{t.desc}</span>
                  </span>
                  <span className="lg:hidden text-sm font-medium whitespace-nowrap">{t.label}</span>
                  <ChevronRight
                    className={cn(
                      'hidden lg:block h-4 w-4 text-muted-foreground/60 transition-all',
                      on ? 'opacity-100' : 'opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0',
                    )}
                  />
                </button>
              );
            })}
          </nav>

          {/* Contenido — cada pestaña renderiza su propia tarjeta .cx (1:1 con el diseño) */}
          <div className="flex-1 min-w-0">{CONTENT[active]}</div>
        </div>
      </div>
    </div>
  );
}
